/**
 * Who changed a market's conditions, for one market on one operating day.
 * Opened from the History link on the notes box in both editors: the current-day
 * drawer and the dated Win the Day editor.
 *
 * Fetches directly rather than through react-query so it behaves the same in
 * both hosts. Each entry shows the state saved at that moment plus what moved
 * since the previous save, so "who changed what" reads off the list.
 */
import { useEffect, useState } from 'react';
import { fetchMarketConditionsHistory, type MarketConditionRevision } from '../services/marketConditionsStore';
import { marketConditionDot, marketConditionNote, marketStatusLabel } from '../utils/marketConditionStatus';

interface Props {
  market: string;
  day: string;
  onClose: () => void;
  /** Bumped by the host after a save so an open panel shows the new revision. */
  refreshKey?: number;
}

const person = (author: string) => (author || '').split('@')[0] || 'Unknown';

const when = (changedAt: string | null) => {
  if (!changedAt) return 'Unknown time';
  const parsed = new Date(changedAt);
  return Number.isNaN(parsed.getTime()) ? changedAt : parsed.toLocaleString();
};

/** True when this revision's note text differs from the one before it. */
export function noteTextChanged(row: MarketConditionRevision, previous?: MarketConditionRevision): boolean {
  return !previous || (row.conditions ?? '') !== (previous.conditions ?? '');
}

/** What moved between a revision and the one saved before it. */
export function changeSummary(row: MarketConditionRevision, previous?: MarketConditionRevision): string[] {
  if (!previous) return ['First entry for this day'];
  const changes: string[] = [];
  if (row.condition_status !== previous.condition_status) {
    changes.push(`Status ${marketStatusLabel(previous.condition_status)} → ${marketStatusLabel(row.condition_status)}`);
  }
  if (noteTextChanged(row, previous)) {
    changes.push(!row.conditions?.trim() ? 'Note cleared' : !previous.conditions?.trim() ? 'Note added' : 'Note edited');
  }
  if ((row.days_out ?? null) !== (previous.days_out ?? null)) {
    changes.push(`Days out ${previous.days_out ?? '—'} → ${row.days_out ?? '—'}`);
  }
  if (row.image_count != null && previous.image_count != null && row.image_count !== previous.image_count) {
    changes.push(`Screenshots ${previous.image_count} → ${row.image_count}`);
  }
  return changes.length ? changes : ['Saved with no changes'];
}

export default function MarketConditionsHistory({ market, day, onClose, refreshKey = 0 }: Props) {
  const [rows, setRows] = useState<MarketConditionRevision[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setRows(null);
    setError('');
    fetchMarketConditionsHistory(market, day)
      .then(result => { if (active) setRows(result); })
      .catch(() => { if (active) setError('Could not load the change history.'); });
    return () => { active = false; };
  }, [market, day, refreshKey]);

  return <section aria-label={`${market} market conditions history`} className="mt-2 rounded-lg border border-theme-border-primary bg-theme-bg-secondary p-3">
    <div className="mb-2 flex items-center justify-between gap-2">
      <h4 className="text-sm font-semibold text-heading">Change history · {market} · {day}</h4>
      <button type="button" onClick={onClose} className="rounded px-2 py-0.5 text-xs font-medium text-muted hover:bg-black/10">Hide</button>
    </div>
    {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    {!rows && !error && <p className="text-sm text-muted">Loading change history…</p>}
    {rows?.length === 0 && <p className="text-sm text-muted">No saved changes for this market on this day.</p>}
    {rows && rows.length > 0 && <ol className="max-h-64 space-y-2 overflow-y-auto">
      {rows.map((row, index) => <li key={row.id} className="rounded-md border border-theme-border-primary bg-theme-bg-primary p-2.5">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <span className="flex items-center gap-1.5 text-sm font-medium text-heading">
            <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ${marketConditionDot[row.condition_status] ?? marketConditionDot.green}`} />
            {person(row.author)}
          </span>
          <span className="text-xs text-muted">{when(row.changed_at)}</span>
        </div>
        <p className="mt-1 text-xs font-medium text-muted">{changeSummary(row, rows[index + 1]).join(' · ')}</p>
        {/* Only reprint the note when this save is what changed it -- otherwise
            every screenshot or status edit repeats the same unchanged text. */}
        {noteTextChanged(row, rows[index + 1])
          && <p className="mt-1 whitespace-pre-wrap break-words text-sm text-body">{marketConditionNote(row.conditions)}</p>}
      </li>)}
    </ol>}
  </section>;
}
